import request from '@/utils/request'

/**
 * 归并管理API服务
 */
export const mergeManagementApi = {
  
  // ==================== 数据获取 ====================
  
  /**
   * 获取未归并的票据列表
   */
  async getUnmergedDocuments(limit = null) {
    const url = limit ? `/ai-merge/documents/unmerged?limit=${limit}` : '/ai-merge/documents/unmerged'
    const response = await fetch('/api' + url, {
      credentials: 'include'
    })
    return await response.json()
  },

  /**
   * 获取未归并的银证列表
   */
  async getUnmergedReceipts(limit = null) {
    const url = limit ? `/ai-merge/receipts/unmerged?limit=${limit}` : '/ai-merge/receipts/unmerged'
    const response = await fetch('/api' + url, {
      credentials: 'include'
    })
    return await response.json()
  },

  /**
   * 获取归并规则列表
   */
  getMergeRules(applicableEntity = null) {
    return request({
      url: '/merge-rules',
      method: 'get',
      params: { applicableEntity }
    })
  },

  /**
   * 获取票据归并组列表
   */
  getDocumentGroups() {
    return request({
      url: '/merge-groups/documents',
      method: 'get'
    })
  },

  /**
   * 获取银证归并组列表
   */
  getReceiptGroups() {
    return request({
      url: '/merge-groups/receipts',
      method: 'get'
    })
  },

  /**
   * 获取统计数据
   */
  getStatistics() {
    return request({
      url: '/statistics/overview',
      method: 'get'
    })
  },

  // ==================== 手动归并 ====================

  /**
   * 手动创建票据归并组
   */
  createDocumentGroup(data) {
    return request({
      url: '/merge-engine/documents/manual-merge',
      method: 'post',
      data: {
        documentIds: data.documentIds,
        groupName: data.groupName
      }
    })
  },

  /**
   * 手动创建银证归并组
   */
  createReceiptGroup(data) {
    return request({
      url: '/merge-engine/receipts/manual-merge',
      method: 'post',
      data: {
        receiptIds: data.receiptIds,
        groupName: data.groupName
      }
    })
  },

  // ==================== 规则归并 ====================

  /**
   * 预览票据规则归并结果
   */
  previewDocumentRuleMerge(ruleId) {
    return request({
      url: '/merge-engine/documents/preview',
      method: 'get',
      params: { ruleId }
    })
  },

  /**
   * 执行票据规则归并
   */
  executeDocumentRuleMerge(data) {
    return request({
      url: '/merge-engine/documents/execute',
      method: 'post',
      data: {
        ruleId: data.ruleId,
        previewOnly: data.previewOnly || false
      }
    })
  },

  /**
   * 预览银证规则归并结果
   */
  previewReceiptRuleMerge(ruleId) {
    return request({
      url: '/merge-engine/receipts/preview',
      method: 'get',
      params: { ruleId }
    })
  },

  /**
   * 执行银证规则归并
   */
  executeReceiptRuleMerge(data) {
    return request({
      url: '/merge-engine/receipts/execute',
      method: 'post',
      data: {
        ruleId: data.ruleId,
        previewOnly: data.previewOnly || false
      }
    })
  },

  // ==================== AI智能归并 ====================

  /**
   * AI分析票据归并建议
   */
  analyzeDocumentMerge(billIds = []) {
    return request({
      url: '/ai-merge/documents/analyze',
      method: 'post',
      data: billIds
    })
  },

  /**
   * AI分析银证归并建议
   */
  analyzeReceiptMerge(receiptIds = []) {
    return request({
      url: '/ai-merge/receipts/analyze',
      method: 'post',
      data: receiptIds
    })
  },

  /**
   * 执行单个AI票据归并建议
   */
  executeSingleDocumentAiMerge(data) {
    return request({
      url: '/ai-merge/documents/execute-single',
      method: 'post',
      data: {
        billIds: data.billIds,
        groupName: data.groupName,
        reason: data.reason
      }
    })
  },

  /**
   * 执行选中的AI票据归并建议
   */
  executeSelectedDocumentAiMerge(selectedGroups) {
    return request({
      url: '/ai-merge/documents/execute-selected',
      method: 'post',
      data: selectedGroups
    })
  },

  /**
   * 执行单个AI银证归并建议
   */
  executeSingleReceiptAiMerge(data) {
    return request({
      url: '/ai-merge/receipts/execute-single',
      method: 'post',
      data: {
        receiptIds: data.receiptIds,
        groupName: data.groupName,
        reason: data.reason
      }
    })
  },

  /**
   * 执行选中的AI银证归并建议
   */
  executeSelectedReceiptAiMerge(selectedGroups) {
    return request({
      url: '/ai-merge/receipts/execute-selected',
      method: 'post',
      data: selectedGroups
    })
  },

  // ==================== 归并组管理 ====================

  /**
   * 获取票据归并组详情
   */
  getDocumentGroupDetail(groupId) {
    return request({
      url: `/merge-groups/documents/${groupId}`,
      method: 'get'
    })
  },

  /**
   * 获取银证归并组详情
   */
  getReceiptGroupDetail(groupId) {
    return request({
      url: `/merge-groups/receipts/${groupId}`,
      method: 'get'
    })
  },

  /**
   * 获取票据归并组内的票据列表
   */
  getDocumentGroupItems(groupId) {
    return request({
      url: `/merge-groups/documents/${groupId}/items`,
      method: 'get'
    })
  },

  /**
   * 获取银证归并组内的银证列表
   */
  getReceiptGroupItems(groupId) {
    return request({
      url: `/merge-groups/receipts/${groupId}/items`,
      method: 'get'
    })
  },

  /**
   * 更新票据归并组信息
   */
  updateDocumentGroup(groupId, data) {
    return request({
      url: `/merge-groups/documents/${groupId}`,
      method: 'put',
      data
    })
  },

  /**
   * 更新银证归并组信息
   */
  updateReceiptGroup(groupId, data) {
    return request({
      url: `/merge-groups/receipts/${groupId}`,
      method: 'put',
      data
    })
  },

  /**
   * 解散票据归并组
   */
  dissolveDocumentGroup(groupId) {
    return request({
      url: `/merge-engine/documents/groups/${groupId}`,
      method: 'delete'
    })
  },

  /**
   * 解散银证归并组
   */
  dissolveReceiptGroup(groupId) {
    return request({
      url: `/merge-engine/receipts/groups/${groupId}`,
      method: 'delete'
    })
  },

  // ==================== 批量操作 ====================

  /**
   * AI自动归并所有未归并数据
   */
  autoMergeAll(limit = 50) {
    return request({
      url: '/ai-merge/auto-merge-all',
      method: 'post',
      params: { limit }
    })
  },

  /**
   * 增量归并新记录
   */
  incrementalMerge(entityId, entityType) {
    return request({
      url: '/merge-engine/incremental-merge',
      method: 'post',
      params: { entityId, entityType }
    })
  },

  /**
   * 查询归并任务状态
   */
  getTaskStatus(taskId) {
    return request({
      url: `/merge-engine/tasks/${taskId}`,
      method: 'get'
    })
  },

  /**
   * 获取票据归并组详情
   */
  getDocumentGroupDetail(groupId) {
    return request({
      url: `/merge-groups/documents/${groupId}`,
      method: 'get'
    })
  },

  /**
   * 获取银证归并组详情
   */
  getReceiptGroupDetail(groupId) {
    return request({
      url: `/merge-groups/receipts/${groupId}`,
      method: 'get'
    })
  },

  /**
   * 获取票据归并组内的票据列表
   */
  getDocumentGroupItems(groupId) {
    return request({
      url: `/merge-groups/documents/${groupId}/items`,
      method: 'get'
    })
  },

  /**
   * 获取银证归并组内的银证列表
   */
  getReceiptGroupItems(groupId) {
    return request({
      url: `/merge-groups/receipts/${groupId}/items`,
      method: 'get'
    })
  }
}

export default mergeManagementApi
